import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiUser,
  FiMail,
  FiPhone,
  FiInstagram,
  FiMapPin,
  FiTrash,
  FiSend,
  FiX,
  FiPaperclip,
  FiImage,
} from "react-icons/fi";
import axios from "axios";
import Spinner from "../layout/Spinner";
import Tooltip from "./Tooltip";

const API_EMAIL = "https://aldanaselena.com/responder.php";
const NAVBAR_HEIGHT = 64;

const ConsultasCard = ({ reserva, onDelete, setModalAbierto }) => {
  const [showEmail, setShowEmail] = useState(false);
  const [showImagen, setShowImagen] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [sending, setSending] = useState(false);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [loadingImage, setLoadingImage] = useState(false);
  const [success, setSuccess] = useState(false);

  // imagen de referencia del cliente (guardada en DB como base64 sin prefijo)
  const imagenSrc = reserva.imagen_base64
    ? `data:image/jpeg;base64,${reserva.imagen_base64}`
    : null;

  useEffect(() => {
    const anyModal = showEmail || showImagen;
    document.body.style.overflow = anyModal ? "hidden" : "auto";
    if (setModalAbierto) setModalAbierto(anyModal);
    return () => {
      document.body.style.overflow = "auto";
      if (setModalAbierto) setModalAbierto(false);
    };
  }, [showEmail, showImagen, setModalAbierto]);

  const compressImage = (file) =>
    new Promise((resolve) => {
      const img = new Image();
      const reader = new FileReader();
      reader.onload = (e) => (img.src = e.target.result);
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const scale = Math.min(600 / img.width, 1);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.6));
      };
      reader.readAsDataURL(file);
    });

  const handleFile = async (e) => {
    const selected = e.target.files[0];
    if (!selected || !selected.type.startsWith("image/")) return;
    setLoadingImage(true);
    const compressed = await compressImage(selected);
    setFile(compressed);
    setFileName(selected.name);
    setLoadingImage(false);
  };

  const handleSend = async () => {
    if (!mensaje.trim()) return;
    try {
      setSending(true);
      const res = await axios.post(API_EMAIL, {
        email: reserva.email,
        nombre: reserva.nombre,
        mensaje,
        imagen: file,
      });
      if (res.data.success) {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          setShowEmail(false);
          setMensaje("");
          setFile(null);
          setFileName("");
        }, 1800);
      }
    } catch (err) {
      console.error(err);
    }
    setSending(false);
  };

  return (
    <>
      {/* ═══════════════════════════════════════ CARD ══ */}
      <motion.div
        layout
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#1C1410]/90 border border-[#3C2B22]/60 rounded-2xl p-5 shadow-xl relative"
      >
        {/* indicador activo */}
        <div className="absolute top-4 right-4">
          <span className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse block" />
        </div>

        {/* NOMBRE */}
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#B8977F]/20">
            <FiUser className="text-[#B8977F]" />
          </div>
          <div>
            <h2 className="text-[#B8977F] text-lg">
              {reserva.nombre} {reserva.apellido}
            </h2>
            <p className="text-xs text-gray-400">Consulta recibida</p>
          </div>
        </div>

        {/* DATOS */}
        <div className="space-y-1 text-sm text-[#EDE0D0]">
          <p className="flex items-center gap-2"><FiMail /> {reserva.email}</p>
          <p className="flex items-center gap-2"><FiPhone /> {reserva.telefono}</p>
          <p className="flex items-center gap-2"><FiInstagram /> @{reserva.instagram}</p>
          <p className="flex items-center gap-2"><FiMapPin /> {reserva.ciudad}</p>
        </div>

        {/* MENSAJE */}
        <div className="mt-4 bg-[#14110F] p-4 rounded-xl text-sm border border-[#4A382F] text-[#F5EDE4] shadow-inner min-h-[60px]">
          {reserva.mensaje || "Sin mensaje"}
        </div>

        {/* IMAGEN DE REFERENCIA */}
        {imagenSrc && (
          <div className="mt-4">
            <p className="text-xs text-[#7A5E50] mb-2 tracking-widest uppercase">
              Referencia
            </p>
            <button
              onClick={() => setShowImagen(true)}
              className="relative group w-full overflow-hidden rounded-xl border border-[#3C2B22]/60"
            >
              <img
                src={imagenSrc}
                alt="referencia"
                className="w-full h-40 object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                <FiImage size={28} className="text-[#B8977F]" />
              </div>
            </button>
          </div>
        )}

        {/* ACCIONES */}
        <div className="flex justify-between items-center mt-4 px-6">
          <Tooltip text="Eliminar consulta">
            <button
              onClick={() => onDelete(reserva.id)}
              className="w-12 h-12 flex items-center justify-center rounded-full border border-red-500 text-red-400 hover:bg-red-500/10 transition shadow-md"
            >
              <FiTrash size={18} />
            </button>
          </Tooltip>

          <Tooltip text="Responder mensaje">
            <button
              onClick={() => setShowEmail(true)}
              className="w-12 h-12 flex items-center justify-center rounded-full bg-[#B8977F] text-black hover:brightness-110 transition shadow-md"
            >
              <FiSend size={18} />
            </button>
          </Tooltip>
        </div>
      </motion.div>

      {/* ════════════════════════ LIGHTBOX IMAGEN ══ */}
      <AnimatePresence>
        {showImagen && imagenSrc && (
          <motion.div
            className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowImagen(false)}
          >
            <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" />

            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              className="relative z-10 max-w-2xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowImagen(false)}
                className="absolute -top-4 -right-4 z-20 w-9 h-9 flex items-center justify-center rounded-full bg-[#1C1410] border border-[#3C2B22] text-[#B8977F] hover:bg-[#3C2B22] transition"
              >
                <FiX size={16} />
              </button>

              <img
                src={imagenSrc}
                alt="referencia ampliada"
                className="w-full rounded-2xl border border-[#3C2B22] shadow-2xl object-contain max-h-[80vh]"
              />

              <p className="text-center text-xs text-[#7A5E50] mt-3 tracking-widest uppercase">
                Imagen de referencia · {reserva.nombre} {reserva.apellido}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══════════════════════════ MODAL EMAIL ══ */}
      <AnimatePresence>
        {showEmail && (
          <motion.div
            className="fixed inset-0 z-[9998] flex justify-center items-center px-3 sm:px-4"
            style={{
              paddingTop: NAVBAR_HEIGHT,
              paddingBottom: "env(safe-area-inset-bottom, 16px)",
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

            <motion.div
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 80, opacity: 0 }}
              transition={{ type: "spring", damping: 20, stiffness: 200 }}
              className="relative w-full max-w-md md:max-w-lg max-h-[80dvh] md:max-h-[85vh] my-4 rounded-2xl bg-[#0E0B09] border border-[#3C2B22]/70 flex flex-col shadow-2xl overflow-hidden"
            >
              {/* HEADER */}
              <div className="flex justify-between items-center px-4 py-4 border-b border-[#3C2B22]">
                <div className="flex flex-col min-w-0">
                  <span className="text-[#B8977F] text-sm flex items-center gap-2">
                    <FiMail /> Responder mensaje
                  </span>
                  <span className="text-xs text-[#EDE0D0]/80 mt-1 truncate">
                    {reserva.nombre} · {reserva.email}
                  </span>
                </div>
                <button
                  onClick={() => setShowEmail(false)}
                  className="p-2 rounded-lg hover:bg-[#1C1410]"
                >
                  <FiX size={18} />
                </button>
              </div>

              {/* BODY */}
              <div className="p-4 space-y-5 flex-1 overflow-y-auto">
                {success ? (
                  <div className="flex justify-center mt-10">
                    <div className="w-14 h-14 rounded-full bg-green-500 flex items-center justify-center text-white text-xl">
                      ✔
                    </div>
                  </div>
                ) : (
                  <>
                    {/* imagen de referencia del cliente dentro del modal */}
                    {imagenSrc && (
                      <div>
                        <p className="text-xs text-[#7A5E50] mb-2 tracking-widest uppercase">
                          Referencia del cliente
                        </p>
                        <img
                          src={imagenSrc}
                          alt="referencia"
                          className="w-full max-h-40 object-cover rounded-xl border border-[#3C2B22]"
                        />
                      </div>
                    )}

                    <textarea
                      value={mensaje}
                      onChange={(e) => setMensaje(e.target.value)}
                      placeholder="Escribí tu mensaje..."
                      className="w-full min-h-[120px] max-h-[200px] bg-[#1C1410] text-[#EDE0D0] p-4 rounded-xl border border-[#3C2B22] resize-none"
                    />

                    <Tooltip text="Adjuntar imagen">
                      <label className="flex items-center gap-2 text-[#7A5E50] hover:text-[#EDE0D0] cursor-pointer">
                        <FiPaperclip />
                        <span className="text-xs">Adjuntar imagen</span>
                        <input type="file" className="hidden" onChange={handleFile} />
                      </label>
                    </Tooltip>

                    {loadingImage && <Spinner />}

                    {file && !loadingImage && (
                      <div className="flex items-center gap-3 bg-[#1C1410] p-2 rounded-lg border border-[#3C2B22]">
                        <img
                          src={file}
                          className="w-14 h-14 object-cover rounded-md flex-shrink-0"
                          alt="adjunto"
                        />
                        <span className="text-xs text-[#B8977F] truncate">
                          {fileName}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* FOOTER */}
              {!success && (
                <div className="flex justify-between items-center px-4 py-3 border-t border-[#3C2B22]">
                  <Tooltip text="Enviar respuesta">
                    <button
                      onClick={handleSend}
                      disabled={sending}
                      className="bg-[#B8977F] w-12 h-12 flex items-center justify-center rounded-full shadow-md"
                    >
                      {sending ? <Spinner size={16} /> : <FiSend />}
                    </button>
                  </Tooltip>

                  <Tooltip text="Cerrar">
                    <button
                      onClick={() => setShowEmail(false)}
                      className="w-12 h-12 flex items-center justify-center border text-[#7A5E50] border-[#7A5E50] rounded-full"
                    >
                      <FiX />
                    </button>
                  </Tooltip>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ConsultasCard;